import {isEqual, uniqBy} from 'lodash';
import memoize from 'memoize-one';
import {compose} from 'redux';
import uuid from 'uuid-random';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {
  createObjectPropertyUpdater,
  createResourceTableValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {AppTask} from '../../../../srcCommon/tasks/appTask';
import {RSendCommentTask} from '../../../../srcCommon/tasks/sendCommentTask';
import {createTask} from '../../../../srcCommon/tasks/task';
import {scheduleOfflineTaskAsyncAction} from '../../../../srcCommon/tasks/taskActions';
import {createCountersUpdaterForConversations} from '../../../helpers/counterHelpers';
import {
  CommentWarning,
  findMentionedAliases,
  findMentionedTeammateIds,
  findMentionedTeammatesForAliases,
  findSendWarnings,
} from '../../../helpers/models/commentHelpers';
import {
  addConversationInbox,
  autoSubscribeToConversation,
  buildConversationUpdater,
  bumpConversationOnSend,
  bumpConversationTracker,
  ConversationStatusesEnum,
  findConversationStatus,
  isConversationInTeamContext,
  isInternalConversation,
  maybeMakeConversationVisible,
  setConversationStatus,
  setConversationSubject,
  updateConversationTrackers,
} from '../../../helpers/models/conversationHelpers';
import {batch} from '../../../helpers/reduxHelpers';
import {CommentModel, CommentStatusesEnum} from '../../../models/api/commentModel';
import {BumpStrategiesEnum} from '../../../models/api/companyPreferencesModel';
import {ConversationTeamStatusesEnum} from '../../../models/api/conversationCommonTypes';
import {ConversationModel} from '../../../models/api/conversationModel';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../../models/api/conversationTrackerModel';
import {DataGroupModel} from '../../../models/api/dataGroupModel';
import {SendActionsEnum} from '../../../models/api/messageModel';
import {StartGuideTaskTypesEnum} from '../../../models/api/preferencesModel';
import {HttpError, HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {findCommentLock, findConversationLock} from '../../../services/locks/resourceLocks';
import {Dispatch, StateGetter} from '../../appStore';
import {ThunkAsyncAction} from '../../appThunk';
import {getHasOnlineClientCounters} from '../../featureFlags/featureFlagsSelectors';
import {
  finishOptimisticUpdateAction,
  startOptimisticUpdateAction,
} from '../../optimisticUpdates/optimisticUpdatesActions';
import {OptimisticUpdater} from '../../optimisticUpdates/optimisticUpdatesState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {updateClientCountersForConversationsAction} from '../counters/countersActions';
import {updateDataAction} from '../dataGroupsActions';
import {getComments, getConversations, getOriginalConversations, getOriginalData} from '../dataSelectors';
import {recordTeammateInteractionsAction} from '../localPreferences/localPreferencesActions';
import {updateStartGuideTasksAction} from '../preferences/preferencesActions';
import {getTeammates, getUserId} from '../teammates/teammatesSelectors';
import {sendCommentApiAsync} from './commentsHelpers';
import {
  saveCommentOfflineAsync,
  saveCommentOnlineAsyncAction,
  tryRemoveCommentOffline,
} from './saveCommentActions';

/*
 * Action creator.
 */

interface SendCommentOptions {
  cellsContext: CellsContext;
  conversationId: number;
  commentUid: string;
  sendAction: SendActionsEnum;
  inboxId?: number;
  ignoreWarnings?: boolean;
  notifyOnThrow?: Handler;
}

export function sendCommentAsyncAction(options: SendCommentOptions): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const hasOnlineClientCounters = getHasOnlineClientCounters(getState());

    // Validate the comment.
    maybeThrowCommentWarnings(getState, options);

    // Create an offline task as a fallback.
    const offlineTask = maybeCreateOfflineTask(getState, options);
    const actionId = offlineTask ? offlineTask.id : uuid();

    // Start an optimistic update.
    const optimisticUpdater = createOptimisticUpdater(getState, hasOnlineClientCounters, options);
    dispatch(startOptimisticUpdateAction(actionId, optimisticUpdater));

    try {
      // Lock the comment so our update is transactionally safe.
      await findCommentLock(options.commentUid).lockAsync(async () => {
        // Find the original version of the comment.
        const state = getState();
        const {userId, comments, teammates} = getOriginalData(state);
        const conversations = getOriginalConversations(state);
        const comment = comments.byId[options.commentUid];
        // TODO: Can't we just use the conversationId on the comment?
        const conversation = conversations[options.conversationId];

        if (!comment || !conversation) {
          dispatch(finishOptimisticUpdateAction(actionId));
          return;
        }

        // Try to send it.
        const mentionedTeammateIds = findMentionedTeammateIds(userId, teammates, comment.text);
        const resultPromise = trySendCommentOnlineOrOfflineAsync(
          dispatch,
          conversation,
          comment,
          mentionedTeammateIds,
          options,
          offlineTask,
        );

        // If user has mentioned a teammate(s), complete the mention teammate start guide task.
        if (mentionedTeammateIds.length > 0) {
          dispatch(updateStartGuideTasksAction(StartGuideTaskTypesEnum.MENTION_TEAMMATE));
        }

        const resultDataGroup = await resultPromise;

        // If we sent offline, we want to keep the optimistic update.
        if (!resultDataGroup) {
          return;
        }

        const finishOptimisticUpdate = () => {
          const resultAction = updateDataAction(resultDataGroup);
          dispatch(finishOptimisticUpdateAction(actionId, resultAction));
        };

        // Update client-side counters until we get a counters pusher update.
        if (hasOnlineClientCounters && resultDataGroup.conversations) {
          dispatch(
            updateClientCountersForConversationsAction(
              resultDataGroup.conversations.map((c) => c.id),
              finishOptimisticUpdate,
            ),
          );
          return;
        }

        // Finish the optimistic update.
        finishOptimisticUpdate();
      });
    } catch (error) {
      // Don't leave the optimistic update hanging around.
      dispatch(finishOptimisticUpdateAction(actionId));

      throw error;
    }
  };
}

/*
 * Helpers.
 */

function maybeThrowCommentWarnings(getState: StateGetter, options: SendCommentOptions) {
  const {conversationId, commentUid, notifyOnThrow} = options;

  // Find the version of the comment the way it is presented to the user.
  const state = getState();
  const userId = getUserId(state);
  const teammates = getTeammates(state);
  const conversations = getConversations(state);
  const comments = getComments(state);

  const comment = comments[commentUid];
  if (!comment) {
    throw new Error('A comment with the specified UID could not be found.');
  }

  const conversation = conversations[conversationId];
  if (!conversation) {
    throw new Error('A conversation with the specified ID could not be found.');
  }

  // Unless specified, check for warnings.
  if (options.ignoreWarnings) {
    return;
  }

  const mentionedAliases = findMentionedAliases(comment.text);
  const mentionedTeammates = findMentionedTeammatesForAliases(userId, teammates, mentionedAliases);
  const warnings = findSendWarnings(state, conversation, comment, mentionedAliases, mentionedTeammates);
  if (warnings.length === 0) {
    return;
  }

  if (notifyOnThrow) {
    notifyOnThrow();
  }

  throw new CommentWarningsError(warnings);
}

function maybeCreateOfflineTask(getState: StateGetter, options: SendCommentOptions): AppTask | undefined {
  const state = getState();
  const {cellsContext, commentUid, conversationId, sendAction} = options;

  // The comment may not exist yet, but we do need the conversation for its URL.
  const conversations = getOriginalConversations(state);
  const conversation = conversations[conversationId];
  if (!conversation) {
    return undefined;
  }

  // Skip if the user is not already tracker.
  const {teammateId} = cellsContext;
  const tracker = conversation.trackers[teammateId];
  if (!tracker) {
    return undefined;
  }

  return createTask(RSendCommentTask, {
    commentUid,
    conversationUrl: conversation.url,
    initiatedAt: Date.now(),
    teammateId,
    lastBumpedAt: tracker.bumpedAt,
    offlineBumpedId: tracker.offlineBumpedId || uuid(),
    status:
      sendAction === SendActionsEnum.SEND_AND_ARCHIVE ? ConversationTrackerStatusesEnum.ARCHIVED : undefined,
    inviteeStage: isInternalConversation(conversation) ? ConversationTrackerStagesEnum.SUBSCRIBER : undefined,
  });
}

function createOptimisticUpdater(
  getState: StateGetter,
  hasOnlineClientCounters: boolean,
  options: SendCommentOptions,
): OptimisticUpdater {
  const {conversationId, commentUid} = options;

  // Make sure sending comments are displayed in order. Once sent, they will appear in order of the server's
  // send date (which means we should complete the API calls in in order).
  const sentAt = Date.now();

  // Create an optimistic updater for the comment.
  const optimisticUpdaterForComment: OptimisticUpdater = createObjectPropertyUpdater(
    'comments',
    createResourceTableValueUpdater(
      commentUid,
      createUpdater((comment) => ({
        ...comment,
        status: CommentStatusesEnum.SENDING,
        sentAt,
      })),
    ),
  );

  const bindOptimisticUpdaterForConversation = memoize(
    (
      bumpStrategy: BumpStrategiesEnum,
      userId: number,
      shouldSubscribeOnComment: boolean,
      newTrackerIds: ReadonlyArray<number>,
      newTopic: string | undefined,
    ): OptimisticUpdater => {
      // Create an optimistic updater for the conversation.
      const optimisticUpdaterForConversationBase: OptimisticUpdater = createObjectPropertyUpdater(
        'conversations',
        createResourceTableValueUpdater(
          conversationId,
          createUpdater((conversation) => {
            const isInternal = isInternalConversation(conversation);
            const newTrackerStage = isInternal ? ConversationTrackerStagesEnum.SUBSCRIBER : undefined;

            const isNewInternal =
              isInternal && conversation.teamStatus === ConversationTeamStatusesEnum.INVISIBLE;
            const shouldAutoSubscribe = isNewInternal || shouldSubscribeOnComment;

            return sendCommentInConversation(
              bumpStrategy,
              userId,
              shouldAutoSubscribe,
              isNewInternal,
              newTrackerIds,
              newTrackerStage,
              newTopic,
              options,
            )(conversation);
          }),
        ),
      );

      // Wrap for optimistic counter updates.
      return hasOnlineClientCounters
        ? createCountersUpdaterForConversations([conversationId], optimisticUpdaterForConversationBase)
        : optimisticUpdaterForConversationBase;
    },
    // Deep compare the arguments, since `newTrackerIds` is a different array instance every time.
    isEqual,
  );

  // Higher-order optimistic updater for the conversation.
  const optimisticUpdaterForConversation: OptimisticUpdater = (data) => {
    const {company, userId, preferences, comments, teammates} = data;
    const comment = comments.byId[commentUid];
    if (!comment) {
      return data;
    }

    const composerTeammateIds = comment.teammateIds || [];
    const mentionedTeammateIds = findMentionedTeammateIds(userId, teammates, comment.text);
    const newTrackerIds = [...mentionedTeammateIds, ...composerTeammateIds];

    return bindOptimisticUpdaterForConversation(
      company.preferences.sendBumpStrategy,
      userId,
      preferences.shouldSubscribeOnComment,
      newTrackerIds,
      comment.topic,
    )(data);
  };

  return compose(optimisticUpdaterForConversation, optimisticUpdaterForComment);
}

async function trySendCommentOnlineOrOfflineAsync(
  dispatch: Dispatch,
  conversation: ConversationModel,
  comment: CommentModel,
  mentionedTeammateIds: ReadonlyArray<number>,
  options: SendCommentOptions,
  offlineTask: AppTask | undefined,
): Promise<DataGroupModel | undefined> {
  try {
    const result = await sendCommentOnlineOrOfflineAsync(
      dispatch,
      conversation,
      comment,
      mentionedTeammateIds,
      options,
      offlineTask,
    );

    // If needed, record the interaction with the mentioned teammates.
    maybeRecordInteractions(dispatch, mentionedTeammateIds);

    return result;
  } catch (error) {
    // Report non-HTTP errors to Bugsnag.
    if (!(error instanceof HttpError || error instanceof SendCommentError)) {
      reportToBugsnag(error);
    }

    // Put the comment in a failed state, so that the comment appears as a red bubble in the timeline instead
    // of going back into the composer.
    const failedComment = {
      ...comment,
      status: CommentStatusesEnum.FAILED,
    };

    return {comments: [failedComment]};
  }
}

async function sendCommentOnlineOrOfflineAsync(
  dispatch: Dispatch,
  conversation: ConversationModel,
  comment: CommentModel,
  mentionedTeammateIds: ReadonlyArray<number>,
  options: SendCommentOptions,
  offlineTask: AppTask | undefined,
): Promise<DataGroupModel | undefined> {
  // If this comment has a save error, allow a retry.
  if (comment.error) {
    throw new SendCommentError('Comment has a save error');
  }

  try {
    // If this comment is in a saving state without a save error, it was saved offline.
    if (comment.isSaving) {
      const savedComment = await dispatch(saveCommentOnlineAsyncAction(comment));

      // This shouldn't happen, but check to avoid a type error or infinite recursion.
      if (!savedComment || savedComment.isSaving) {
        throw new Error('Saved comment could not be found in data group');
      }

      // Recurse with the saved comment.
      return sendCommentOnlineOrOfflineAsync(
        dispatch,
        conversation,
        savedComment,
        mentionedTeammateIds,
        options,
        offlineTask,
      );
    }

    // Try to send online.
    const result = await sendCommentOnlineAsync(conversation, comment, mentionedTeammateIds, options);

    // Clean up client-side storage, since we may have saved the comment offline with a failed state.
    tryRemoveCommentOffline(comment);

    return result;
  } catch (error) {
    // Can we send offline instead?
    const isOfflineError = error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
    if (!isOfflineError || !offlineTask) {
      throw error;
    }

    // Try to save offline with a failed state first, so that if all else fails the comment appears as a red
    // bubble in the timeline instead of going back into the composer, even after a restart.
    const saveDataGroup = await saveCommentOfflineAsync(
      {
        ...comment,
        status: CommentStatusesEnum.FAILED,
      },
      error,
    );

    await batch(async () => {
      dispatch(updateDataAction(saveDataGroup));

      // Try to send offline. This will replace the optimistic update.
      await dispatch(scheduleOfflineTaskAsyncAction(offlineTask, error));
    });

    return undefined;
  }
}

async function sendCommentOnlineAsync(
  conversation: ConversationModel,
  comment: CommentModel,
  mentionedTeammateIds: ReadonlyArray<number>,
  options: SendCommentOptions,
): Promise<DataGroupModel> {
  const {cellsContext, sendAction} = options;
  const shouldArchive = sendAction === SendActionsEnum.SEND_AND_ARCHIVE;

  const isInTeamContext = isConversationInTeamContext(cellsContext, conversation);
  const shouldArchiveForTeam = shouldArchive && isInTeamContext;
  const shouldArchiveForUser = shouldArchive && !isInTeamContext;

  const composerTeammateIds = comment.teammateIds || [];
  const newTrackerIds = [...mentionedTeammateIds, ...composerTeammateIds];

  const isInternal = isInternalConversation(conversation);
  const newTrackerStage = isInternal ? ConversationTrackerStagesEnum.SUBSCRIBER : undefined;

  const trackers = buildServerTrackers(
    cellsContext.teammateId,
    shouldArchiveForUser,
    newTrackerIds,
    newTrackerStage,
  );

  const body = {
    type: 'comment',
    comment: {uid: comment.uid},
    meta: {
      archive: shouldArchiveForTeam || undefined,
      subject: comment.topic,
      trackers,
      inbox_id: options.inboxId,
    },
  };

  // Make sure comments on the same conversation are sent in order.
  // TODO: Widen the scope of this lock, since our behavior is dependent on the conversation state?
  return findConversationLock(conversation.id).lockAsync(async () =>
    sendCommentApiAsync(conversation.url, comment, body),
  );
}

/*
 * Conversation updater.
 */

function sendCommentInConversation(
  bumpStrategy: BumpStrategiesEnum,
  userId: number,
  shouldAutoSubscribe: boolean,
  shouldForceBump: boolean,
  newTrackerIds: ReadonlyArray<number>,
  newTrackerStage: ConversationTrackerStagesEnum | undefined,
  newTopic: string | undefined,
  options: SendCommentOptions,
) {
  const {cellsContext, sendAction} = options;
  const shouldArchive = sendAction === SendActionsEnum.SEND_AND_ARCHIVE;

  return buildConversationUpdater(
    // If needed, become a subscriber of this conversation.
    shouldAutoSubscribe && autoSubscribeToConversation(userId),
    // If needed, bump the conversation.
    bumpConversationOnSend(
      bumpStrategy,
      BumpStrategiesEnum.COMMENT,
      cellsContext.teammateId,
      shouldForceBump,
    ),
    // Add the mentioned teammates.
    updateConversationTrackers({add: newTrackerIds, stage: newTrackerStage}),
    // If needed, update the subject.
    newTopic && setConversationSubject(newTopic),
    // If needed, set the inbox.
    options.inboxId && addConversationInbox(options.inboxId),
    // If needed, make the conversation visible.
    maybeMakeConversationVisible(),
    (conversation) => {
      // If we don't need to archive in the first place, nothing to do.
      if (!shouldArchive) {
        return conversation;
      }

      // If the conversation is already archived, nothing to do.
      const status = findConversationStatus(cellsContext, conversation);
      if (status === ConversationStatusesEnum.ARCHIVED) {
        return conversation;
      }

      // Bump the conversation for the tracker and update the status.
      const isInTeamContext = isConversationInTeamContext(cellsContext, conversation);
      return buildConversationUpdater(
        // If needed, bump the conversation for the tracker.
        !isInTeamContext && bumpConversationTracker(cellsContext.teammateId),
        // Archive the conversation
        setConversationStatus(cellsContext, ConversationStatusesEnum.ARCHIVED),
      )(conversation);
    },
  );
}

function buildServerTrackers(
  teammateId: number,
  shouldArchiveForTeammate: boolean,
  newTrackerIds: ReadonlyArray<number>,
  newTrackerStage?: ConversationTrackerStagesEnum,
) {
  // Deal with the new trackers that we're adding to the conversation.
  const newTrackers = newTrackerIds.map((id) => ({
    teammate_id: id,
    status: ConversationTrackerStatusesEnum.INBOX,
    stage: newTrackerStage,
  }));

  // If there's no need to archive for our teammate, stop here.
  if (!shouldArchiveForTeammate) {
    return newTrackers;
  }

  // Otherwise, add a tracker for our teammate.
  const teammateTracker = {
    teammate_id: teammateId,
    status: ConversationTrackerStatusesEnum.ARCHIVED,
    bump: true,
  };

  return uniqBy([teammateTracker, ...newTrackers], (t) => t.teammate_id);
}

function maybeRecordInteractions(dispatch: Dispatch, mentionedTeammateIds: ReadonlyArray<number>) {
  if (mentionedTeammateIds.length === 0) {
    return;
  }

  dispatch(recordTeammateInteractionsAction(mentionedTeammateIds));
}

class SendCommentError extends CustomError {}

/** Custom error containing a list of CommentWarning. */
export class CommentWarningsError extends SendCommentError {
  constructor(warnings: ReadonlyArray<CommentWarning>) {
    super('Warn user before sending');
    this.warnings = warnings;
  }

  readonly warnings: ReadonlyArray<CommentWarning>;
}
